import { useState, useRef, useEffect } from "react";
import TiltCard from "@/components/base/TiltCard";
import { SITE_MEDIA } from "@/constants/siteMedia";

const cards = [
  {
    icon: "ri-brain-line",
    title: "Ava remembers what matters to you",
    description: "Every conversation builds on the last. Ava keeps track of what you share — your struggles, your wins, your story — so you never have to start from zero.",
    iconBg: "#ede8ff",
    iconColor: "#7C3AED",
    barColor: "#7C3AED",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Ava responds in a way that actually feels human",
    description: "Not scripted replies. Not generic advice. Ava reads between the lines and answers with tone, empathy, and care — so you feel truly heard, not just responded to.",
    iconBg: "#ffe8f0",
    iconColor: "#e11d48",
    barColor: "#e11d48",
  },
  {
    icon: "ri-moon-line",
    title: "Available 24/7 — no appointments, no waiting",
    description: "No scheduling. No waiting rooms. No 'business hours.' Ava is here at 2am, on weekends, whenever your mind won't quiet down and you need someone there.",
    iconBg: "#e8f0ff",
    iconColor: "#6366f1",
    barColor: "#6366f1",
  },
  {
    icon: "ri-links-line",
    title: "Built for connection, not productivity",
    description: "Ava isn't a task manager or a search engine. She's built for one thing: making you feel less alone. Every line of code serves that purpose.",
    iconBg: "#fff8e8",
    iconColor: "#d97706",
    barColor: "#d97706",
  },
  {
    icon: "ri-mic-line",
    title: "Voice, text, or just presence — however you need",
    description: "Talk out loud when you need to vent. Type when it's quieter. Sometimes just knowing someone is there is enough. Ava meets you where you are.",
    iconBg: "#e8fff4",
    iconColor: "#059669",
    barColor: "#059669",
  },
];

export default function WhyAva() {
  const [hovered, setHovered] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("why-visible");
        });
      },
      { threshold: 0.1 }
    );
    const items = sectionRef.current?.querySelectorAll(".why-animate");
    items?.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="why-ava"
      className="py-14 md:py-20"
      style={{ background: "linear-gradient(180deg, #f5f0ff 0%, #ede8ff 60%, #f0eeff 100%)" }}
    >
      <style>{`
        @keyframes whyBotFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-14px); }
        }
        @keyframes badge1Float {
          0%, 100% { transform: translateY(0px) rotate(-2deg); }
          50% { transform: translateY(-8px) rotate(2deg); }
        }
        @keyframes badge2Float {
          0%, 100% { transform: translateY(0px) rotate(2deg); }
          50% { transform: translateY(8px) rotate(-2deg); }
        }
        .why-animate {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .why-animate.why-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .why-animate:nth-child(1) { transition-delay: 0s; }
        .why-animate:nth-child(2) { transition-delay: 0.1s; }
        .why-animate:nth-child(3) { transition-delay: 0.2s; }
        .why-animate:nth-child(4) { transition-delay: 0.3s; }
        .why-card-hover {
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .why-card-hover:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 32px rgba(124,58,237,0.10);
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-[#f5f0ff] text-[#7C3AED] text-sm font-medium mb-4">
            <i className="ri-star-line"></i>
            WHY AVA EXISTS
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
            Ava was created for people who needed
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              Someone to talk to—and didn't always have someone there.
            </span>
          </h2>
          <p className="text-[#5a4a8a] text-base max-w-xl mx-auto">
            This isn't just another AI tool. It's a space to think, reflect, and be heard.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
          <div className="w-full lg:w-[320px] flex-shrink-0 flex justify-center relative">
            <div className="relative w-64 h-72 flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-full blur-3xl opacity-20"
                style={{ background: "radial-gradient(circle, #A855F7 0%, #7C3AED 60%, transparent 80%)" }}
              />
              <img
                src={SITE_MEDIA.avaIllustration}
                alt="Ava AI Bot"
                className="relative z-10 w-56 h-56 object-contain"
                style={{
                  filter: "drop-shadow(0 0 32px rgba(168,85,247,0.35))",
                  animation: "whyBotFloat 3.2s ease-in-out infinite",
                }}
              />
              <div
                className="absolute top-4 -left-4 bg-white rounded-2xl px-4 py-2.5 border border-[#e0d8ff] z-20"
                style={{ animation: "badge1Float 2.8s ease-in-out infinite" }}
              >
                <p className="text-lg font-extrabold text-[#7C3AED]">10k+</p>
                <p className="text-xs text-[#7a6aaa]">Conversations</p>
              </div>
              <div
                className="absolute bottom-10 -right-4 bg-white rounded-2xl px-4 py-2.5 border border-[#e0d8ff] z-20"
                style={{ animation: "badge2Float 3.4s ease-in-out infinite" }}
              >
                <p className="text-lg font-extrabold text-[#A855F7]">5★</p>
                <p className="text-xs text-[#7a6aaa]">avg rating</p>
              </div>
            </div>
          </div>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {cards.map((card, i) => (
              <TiltCard
                key={i}
                className="why-animate"
                tiltAmount={8}
                scale={1.02}
              >
                <div
                  className="why-card-hover bg-white rounded-2xl p-6 border cursor-default relative overflow-hidden h-full"
                  style={{ borderColor: hovered === i ? card.iconColor + "40" : "#ede8ff" }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl mb-4 transition-all duration-300"
                    style={{ background: hovered === i ? card.iconColor : card.iconBg }}
                  >
                    <i
                      className={`${card.icon} text-xl transition-colors duration-300`}
                      style={{ color: hovered === i ? "#ffffff" : card.iconColor }}
                    ></i>
                  </div>
                  <h3 className="font-bold text-[#1a1040] text-base mb-2">{card.title}</h3>
                  <p className="text-[#7a6aaa] text-sm leading-relaxed">{card.description}</p>
                  <div
                    className="absolute bottom-0 left-0 h-[3px] rounded-full transition-all duration-500"
                    style={{ background: card.barColor, width: hovered === i ? "100%" : "32px" }}
                  />
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}